const isSSR = typeof window === 'undefined';

// Only grab the “context” (and set up the loader configuration) on the client
let context = {};
let loader = null;
let loaderPromise = null;

// A simple cache of “hasStreetView” results or “loading” states
const streetViewCheckList = {}; // { [key: string]: boolean | 'loading' }
const callbacksMap = {}; // { [key: string]: Array<Function> }

/**
 * Lazily loads the Google Maps JS API. Uses dynamic import so the @googlemaps/js-api-loader
 * chunk is deferred until first use (e.g. when user types in search/address), improving
 * LCP and TBT. On SSR, returns a resolved promise.
 */
export function loadGMap() {
    if (isSSR) {
        return Promise.resolve();
    }
    if (loader) {
        return loader.load();
    }
    if (!loaderPromise) {
        loaderPromise = import('@googlemaps/js-api-loader').then(({ Loader }) => {
            context = window.__INITIAL_STATE__ || {};
            const loaderConfig = {
                version: 'weekly',
                libraries: ['geometry', 'places'],
            };
            const credential = context.googleMapKey || '';
            // Production historically forced client=. Honor the credential itself so
            // key= works in every environment and client= keeps working until cutover.
            if (credential.startsWith('client=')) {
                loaderConfig.client = credential.replace(/^client=/, '');
            } else {
                loaderConfig.apiKey = credential.replace(/^key=/, '');
            }
            loader = new Loader(loaderConfig);
            return loader;
        });
    }
    return loaderPromise.then((l) => l.load());
}

/**
 * Checks whether Street View imagery exists at a given location.
 *
 * @param {Object} opt
 * @param {string} [opt.address] – e.g. "1600 Amphitheatre Parkway, Mountain View, CA"
 * @param {number} [opt.lat] – latitude
 * @param {number} [opt.lng] – longitude
 * @param {Function} opt.callback – function(boolean) to receive true/false
 */
export function hasStreetView(opt) {
    if (isSSR) {
        // Immediately invoke callback(false) on the server
        if (typeof opt.callback === 'function') {
            opt.callback(false);
        }
        return;
    }

    return loadGMap().then(() => {
        let key = null;

        // Determine the cache key; lat/lng takes priority
        if (opt.lat != null && opt.lng != null) {
            key = `${opt.lat},${opt.lng}`;
        } else if (opt.address) {
            key = encodeURIComponent(opt.address);
        } else {
            // Neither lat/lng nor address provided
            opt.callback(false);
            return;
        }

        const cached = streetViewCheckList[key];
        if (cached !== undefined) {
            // If we already have a boolean → call immediately
            if (cached !== 'loading') {
                opt.callback(cached);
                return;
            }
            // Otherwise, status is 'loading' → just queue the callback
            callbacksMap[key].push(opt.callback);
            return;
        }

        // First request for this key: mark as loading and initialize callback queue
        streetViewCheckList[key] = 'loading';
        callbacksMap[key] = [opt.callback];

        const metaDataPath = `/maps/api/streetview/metadata?location=${key}&${context.googleMapKey}`;
        const body = { url: metaDataPath };
        fetch(`${context.appUrl}webservices/getgooglemapurlwithsignature/`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json; charset=UTF-8' },
            body: JSON.stringify(body),
        })
            .then((response) => response.json())
            .then((data) => {
                if (!data.url) {
                    throw new Error('unsigned street view metadata');
                }
                return fetch(`https://maps.googleapis.com${data.url}`, {
                    method: 'GET',
                });
            })
            .then((response) => response.json())
            .then((res) => {
                const hasSV = res && res.status === 'OK';
                streetViewCheckList[key] = hasSV;
                callbacksMap[key].forEach((cb) => cb(hasSV));
                delete callbacksMap[key];
            })
            .catch(() => {
                streetViewCheckList[key] = false;
                callbacksMap[key].forEach((cb) => cb(false));
                delete callbacksMap[key];
            });
    });
}
